import React, { useEffect, useState } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import rehypeRaw from "rehype-raw";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { Card, PageHeader, Button, Spinner } from "@/components/ui";
import { Download, FileText, Lock, Share2 } from "lucide-react";

const VERSIONS = {
  internal: {
    label: "Internal (full)",
    mdUrl: "/docs/GoMCO-AI-Operated-Model.md",
    pdfKey: "ai-operated-model",
    pdfName: "GoMCO-AI-Operated-Model.pdf",
  },
  external: {
    label: "Shareable (reframed)",
    mdUrl: "/docs/GoMCO-AI-Augmented-Delivery-External.md",
    pdfKey: "ai-augmented-external",
    pdfName: "GoMCO-AI-Augmented-Delivery-External.pdf",
  },
};

export default function AiOperatingModel() {
  const [view, setView] = useState("internal");
  const [md, setMd] = useState(null);
  const v = VERSIONS[view];

  useEffect(() => {
    setMd(null);
    fetch(v.mdUrl).then((r) => r.text()).then(setMd).catch(() => setMd("Failed to load document."));
  }, [v.mdUrl]);

  const downloadPdf = async () => {
    try {
      const res = await api.get(`/docs/${v.pdfKey}/pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      const a = document.createElement("a");
      a.href = url; a.download = v.pdfName; a.click();
      URL.revokeObjectURL(url);
    } catch { toast.error("PDF export failed"); }
  };

  const isExternal = view === "external";

  return (
    <div className="vc-fade-up max-w-5xl">
      <PageHeader
        title="AI-Operated Lean Model"
        subtitle="The internal economics vs. the Trillium-safe reframe. Toggle to compare — and know exactly what to submit."
        actions={<div className="flex gap-2">
          <Button variant="outline" onClick={downloadPdf} data-testid="ai-download-pdf"><FileText className="h-4 w-4" /> PDF</Button>
          <a href={v.mdUrl} download><Button data-testid="ai-download-md"><Download className="h-4 w-4" /> Markdown</Button></a>
        </div>}
      />

      {/* Version toggle */}
      <div className="inline-flex rounded-lg border border-border p-1 mb-4" data-testid="ai-version-toggle">
        {Object.entries(VERSIONS).map(([k, cfg]) => (
          <button key={k} onClick={() => setView(k)} data-testid={`ai-view-${k}`}
            className={`flex items-center gap-1.5 px-4 py-1.5 text-sm font-medium rounded-md transition-colors ${view === k ? "bg-brand-700 text-white" : "text-muted-foreground hover:text-foreground"}`}>
            {k === "internal" ? <Lock className="h-3.5 w-3.5" /> : <Share2 className="h-3.5 w-3.5" />}
            {cfg.label}
          </button>
        ))}
      </div>

      {/* Context banner */}
      {isExternal ? (
        <div className="mb-6 rounded-md bg-indigo-50 border border-indigo-200 px-4 py-3 flex items-start gap-2 text-sm text-indigo-800" data-testid="ai-banner-external">
          <Share2 className="h-4 w-4 mt-0.5 shrink-0" />
          <span><strong>Safe to submit to Trillium.</strong> This is the reframed version — "AI-augmented delivery under licensed human oversight." It intentionally omits FTE counts, cost-to-serve, and margins. <strong>This is the one you hand over.</strong></span>
        </div>
      ) : (
        <div className="mb-6 rounded-md bg-red-50 border border-red-200 px-4 py-3 flex items-start gap-2 text-sm text-red-700" data-testid="ai-banner-internal">
          <Lock className="h-4 w-4 mt-0.5 shrink-0" />
          <span><strong>Internal only — never share.</strong> This version exposes the ~5-FTE human core, cost-to-serve, and margins. Use it for planning. To submit, switch to <strong>Shareable (reframed)</strong>.</span>
        </div>
      )}

      {/* Quick comparison */}
      <Card className="p-5 mb-6" data-testid="ai-compare">
        <h3 className="font-display font-semibold mb-3">What changes between the two versions</h3>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
          <div className="rounded-md border border-red-200 bg-red-50/50 p-3">
            <p className="font-semibold text-red-700 flex items-center gap-1.5 mb-2"><Lock className="h-3.5 w-3.5" /> Internal shows</p>
            <ul className="space-y-1 text-foreground list-disc ml-4">
              <li>Exact human-core headcount (~5 FTE)</li>
              <li>Loaded salaries &amp; annual labor cost</li>
              <li>AI/infra spend and cost-to-serve</li>
              <li>Gross margin (~84%)</li>
              <li>Implementation labor comparison</li>
            </ul>
          </div>
          <div className="rounded-md border border-indigo-200 bg-indigo-50/50 p-3">
            <p className="font-semibold text-indigo-700 flex items-center gap-1.5 mb-2"><Share2 className="h-3.5 w-3.5" /> Shareable keeps</p>
            <ul className="space-y-1 text-foreground list-disc ml-4">
              <li>AI-augmented, human-accountable approach</li>
              <li>Where AI helps vs. where humans decide</li>
              <li>Benefits: speed, quality, 24×7, scale</li>
              <li>Safety, governance &amp; auditability (HITL)</li>
              <li>Compliance &amp; SLA accountability</li>
            </ul>
          </div>
        </div>
      </Card>

      <Card className="p-6 sm:p-10">
        {md === null ? <Spinner label="Loading…" /> : (
          <div className="rfp-prose" data-testid="ai-model-content">
            <ReactMarkdown remarkPlugins={[remarkGfm]} rehypePlugins={[rehypeRaw]}>{md}</ReactMarkdown>
          </div>
        )}
      </Card>
    </div>
  );
}
